import { PerformanceTelemetry } from '@/services/PerformanceTelemetry';
import { SimulationConfig } from '@/types';

/**
 * Assigns a unique ID to any object (such as a GPUBuffer) to enable efficient, 
 * zero-allocation caching keys based on actual resource references.
 */
export function getUniqueResourceId(resource: any): string {
  if (!resource) return 'null';
  if (resource._uniqueId === undefined) {
    resource._uniqueId = Math.random().toString(36).substr(2, 9) + '_' + Date.now();
  }
  return resource._uniqueId;
}

/**
 * PersistentBufferRing implements a triple-buffered ring of write-mapped buffers.
 * To avoid the expensive overhead of queue.writeBuffer (which performs copies on the CPU/driver),
 * this system uses mapAsync on a rotated pool of buffers. 
 * While buffer i is in-flight on the GPU, buffer (i+1) is already mapped on the CPU,
 * achieving true zero-copy CPU-to-GPU memory transfer capability with no stalling.
 */
export class PersistentBufferRing {
  private device: any;
  private label: string;
  private size: number;
  private usage: number;
  private buffers: any[] = [];
  private currentIndex = 0;
  private mappedStates: boolean[] = [];

  constructor(device: any, label: string, size: number, usage: number) {
    this.device = device;
    this.label = label;
    this.size = size;
    // We add MAP_WRITE capability to allow CPU mapping, and also verify COPY_SRC/DST is linked
    this.usage = usage | 0x0008 /* COPY_DST */ | 0x0010 /* MAP_WRITE */;

    this.allocateRing();
  }

  private allocateRing() {
    this.dispose();
    this.buffers = [];
    this.mappedStates = [];
    this.currentIndex = 0;

    // Allocate triple-buffered ring with mappedAtCreation = true
    for (let i = 0; i < 3; i++) {
      const buffer = this.device.createBuffer({
        label: `${this.label}_ring_slot_${i}`,
        size: this.size,
        usage: this.usage,
        mappedAtCreation: true
      });
      this.buffers.push(buffer);
      this.mappedStates.push(true);
      PerformanceTelemetry.vramDetails.storageBuffers += this.size;
    }
  }

  public getActiveBuffer(): any {
    return this.buffers[this.currentIndex];
  }

  /**
   * Writes data directly to the current mapped buffer range.
   * Seamlessly falls back to writeBuffer if mapping states aren't ready.
   */
  public writeData(arrayData: ArrayBufferView): any {
    // If layout is too small, resize the whole ring immediately
    if (arrayData.byteLength > this.size) {
      this.resize(Math.max(arrayData.byteLength, this.size * 2));
    }

    const activeBuf = this.buffers[this.currentIndex];
    const isMapped = this.mappedStates[this.currentIndex];

    if (isMapped) {
      try {
        const mappedRange = activeBuf.getMappedRange(0, arrayData.byteLength);
        const typedTarget = new Uint8Array(mappedRange);
        const typedSource = new Uint8Array(arrayData.buffer, arrayData.byteOffset, arrayData.byteLength);
        typedTarget.set(typedSource);
        
        activeBuf.unmap();
        this.mappedStates[this.currentIndex] = false;
        
        PerformanceTelemetry.bytesUploaded += arrayData.byteLength;
      } catch (err) {
        // Fallback to queue.writeBuffer if getMappedRange fails unexpectedly
        this.device.queue.writeBuffer(activeBuf, 0, arrayData);
        PerformanceTelemetry.bytesUploaded += arrayData.byteLength;
      }
    } else {
      // Fallback if not currently mapped
      this.device.queue.writeBuffer(activeBuf, 0, arrayData);
      PerformanceTelemetry.bytesUploaded += arrayData.byteLength;
    }

    const returnedBuffer = activeBuf;

    // Advance index in ring
    const completedIdx = this.currentIndex;
    this.currentIndex = (this.currentIndex + 1) % this.buffers.length;

    // Re-map the completed buffer immediately back to mapAsync WRITE state,
    // so it will be ready when we rotate back to it.
    const reMapBuffer = this.buffers[completedIdx];
    reMapBuffer.mapAsync(2 /* GPUMapMode.WRITE (0x2) */).then(() => {
      this.mappedStates[completedIdx] = true;
    }).catch(() => {
      this.mappedStates[completedIdx] = false;
    });

    return returnedBuffer;
  }

  public resize(newSize: number) {
    const oldSize = this.size;
    this.size = newSize;
    
    // De-allocate old resources
    this.buffers.forEach(b => {
      try {
        PerformanceTelemetry.vramDetails.storageBuffers -= oldSize;
        b.destroy();
      } catch (err) {}
    });

    this.allocateRing();
  }

  public dispose() {
    this.buffers.forEach(b => {
      try {
        PerformanceTelemetry.vramDetails.storageBuffers -= this.size;
        b.destroy();
      } catch (err) {}
    });
    this.buffers = [];
    this.mappedStates = [];
  }
}

/**
 * Smart GPU bind group caching mechanism that persists across frames.
 * Keyed by active underlying buffer allocations, preventing garbage collection ticks.
 */
export class GPUBindGroupCache {
  private device: any;
  private cache = new Map<string, any>();

  constructor(device: any) {
    this.device = device;
  }

  public getOrCreateBindGroup(cacheKey: string, layout: any, entries: any[]): any {
    // Generate an absolute unique key based on bind layout and precise buffer IDs
    const entryIds = entries.map(e => {
      if (e.resource && e.resource.buffer) {
        return `${e.binding}:${getUniqueResourceId(e.resource.buffer)}`;
      }
      return `${e.binding}:sampler_or_texture`;
    }).join('|');
    
    const absoluteKey = `${cacheKey}::${entryIds}`;

    if (this.cache.has(absoluteKey)) {
      return this.cache.get(absoluteKey);
    }

    const bindGroup = this.device.createBindGroup({ layout, entries });
    this.cache.set(absoluteKey, bindGroup);
    return bindGroup;
  }

  public clear() {
    this.cache.clear();
  }
}

/**
 * Captures pre-recorded draw nodes for redundant elements (like static borders)
 * into reusable GPURenderBundles, eliminating frame recording loops.
 */
export class WebGPURenderBundleCache {
  private device: any;
  private cache = new Map<string, any>();

  constructor(device: any) {
    this.device = device;
  }

  public getOrCreateBundle(
    key: string,
    colorFormat: string,
    recordFn: (encoder: any) => void
  ): any {
    if (this.cache.has(key)) {
      return this.cache.get(key);
    }

    // GPURenderBundleEncoderDescriptor requires colorFormats
    const encoder = this.device.createRenderBundleEncoder({
      colorFormats: [colorFormat]
    });
    
    recordFn(encoder);
    
    const bundle = encoder.finish({ label: `Precompiled_Bundle_${key}` });
    this.cache.set(key, bundle);
    return bundle;
  }

  public clear() {
    this.cache.clear();
  }
}

/**
 * Task 9.1: Dynamic, async background texture and material streaming manager.
 * Spawns an off-thread inline Web Worker to fetch and decode images via ImageBitmap,
 * uploading them to WebGPU textures with zero main-thread CPU stutters.
 */
export class WebGPUTextureStreamer {
  private device: any;
  private worker: Worker | null = null;
  private textures = new Map<string, any>(); // url -> GPUTexture
  private pendingRequests = new Map<string, Array<{ resolve: (tex: any) => void; reject: (err: any) => void }>>();

  constructor(device: any) {
    this.device = device;
    this.initWorker();
  }

  private initWorker() {
    try {
      // Create a background worker that fetches and decodes using createImageBitmap asynchronously
      const workerBlobCode = `
        self.onmessage = async (e) => {
          const { url } = e.data;
          try {
            const response = await fetch(url);
            const blob = await response.blob();
            const imageBitmap = await createImageBitmap(blob);
            self.postMessage({ url, success: true, imageBitmap }, [imageBitmap]);
          } catch (err) {
            self.postMessage({ url, success: false, error: err.message });
          }
        };
      `;
      const blob = new Blob([workerBlobCode], { type: 'application/javascript' });
      this.worker = new Worker(URL.createObjectURL(blob));
      this.worker.onmessage = (e) => {
        const { url, success, imageBitmap, error } = e.data;
        const callbacks = this.pendingRequests.get(url);
        if (!callbacks) return;

        this.pendingRequests.delete(url);

        if (success && imageBitmap) {
          try {
            const texture = this.uploadImageBitmapToTexture(imageBitmap, url);
            callbacks.forEach(cb => cb.resolve(texture));
          } catch (uploadErr) {
            callbacks.forEach(cb => cb.reject(uploadErr));
          } finally {
            imageBitmap.close();
          }
        } else {
          const fallbackErr = new Error(error || 'Failed to stream decode asset off-thread');
          callbacks.forEach(cb => cb.reject(fallbackErr));
        }
      };
    } catch (err) {
      console.warn('Failed to boot WebGPU Texture Streamer off-thread Web Worker. Resorting to main-thread.', err);
    }
  }

  private uploadImageBitmapToTexture(imageBitmap: ImageBitmap, label = 'StreamedMaterial'): any {
    const texture = this.device.createTexture({
      label: `Stream_Texture_${label}`,
      size: [imageBitmap.width, imageBitmap.height, 1],
      format: 'rgba8unorm',
      usage: 0x0002 /* COPY_DST */ | 0x0010 /* TEXTURE_BINDING */ | 0x0004 /* COPY_SRC */ | 0x0100 /* RENDER_ATTACHMENT */
    });

    this.device.queue.copyExternalImageToTexture(
      { source: imageBitmap, flipY: false },
      { texture: texture },
      [imageBitmap.width, imageBitmap.height, 1]
    );

    // Track VRAM allocation safely
    const estimatedBytes = imageBitmap.width * imageBitmap.height * 4;
    PerformanceTelemetry.vramDetails.texturesAndFB += estimatedBytes;

    this.textures.set(label, texture);
    return texture;
  }

  public streamTexture(url: string): Promise<any> {
    if (this.textures.has(url)) {
      return Promise.resolve(this.textures.get(url));
    }

    if (this.pendingRequests.has(url)) {
      return new Promise((resolve, reject) => {
        this.pendingRequests.get(url)!.push({ resolve, reject });
      });
    }

    return new Promise((resolve, reject) => {
      this.pendingRequests.set(url, [{ resolve, reject }]);

      if (this.worker) {
        this.worker.postMessage({ url });
      } else {
        // Safe main thread fallback if Worker context is unsupported
        fetch(url)
          .then(res => res.blob())
          .then(blob => createImageBitmap(blob))
          .then(imageBitmap => {
            const texture = this.uploadImageBitmapToTexture(imageBitmap, url);
            imageBitmap.close();
            resolve(texture);
          })
          .catch(err => {
            this.pendingRequests.delete(url);
            reject(err);
          });
      }
    });
  }

  public getTexture(url: string): any | undefined {
    return this.textures.get(url);
  }

  public dispose() {
    if (this.worker) {
      this.worker.terminate();
    }
    this.textures.forEach(tex => {
      try {
        tex.destroy();
      } catch (err) {}
    });
    this.textures.clear();
    this.pendingRequests.clear();
  }
}

/**
 * Task 9.3: Strict WebGPU VRAM budget gatekeeper and adaptive content scaler.
 * Tracks total buffer, texture, swapchain, and query telemetry allocations in real-time.
 * Dynamically sheds graphic load, shortens trail bounds, drops particle emissions,
 * and forces aggressive LOD modes if VRAM nears safety limits.
 */
export class WebGPUVRAMGatekeeper {
  // Budget limit of 250MB for general sandboxed operations
  public static budgetLimit = 250 * 1024 * 1024;
  public static warningThreshold = 0.70; // 70%
  public static criticalThreshold = 0.85; // 85%

  public static adjustSettingsToBudget(config: SimulationConfig) {
    const currentBytes = PerformanceTelemetry.vramDetails.vertexBuffers +
                          PerformanceTelemetry.vramDetails.indexBuffers +
                          PerformanceTelemetry.vramDetails.uniformBuffers +
                          PerformanceTelemetry.vramDetails.storageBuffers +
                          PerformanceTelemetry.vramDetails.texturesAndFB +
                          PerformanceTelemetry.vramDetails.queryBuffers;

    const utilization = currentBytes / this.budgetLimit;

    if (utilization >= this.criticalThreshold) {
      // Critical strain: enforce hard limits immediately
      if (config.gpuParticlesEnabled !== false) {
        (config as any).collisionEffectParticleCount = 4;
        (config as any).maxParticles = 500;
      }
      if ((config as any).backgroundStyle && (config as any).backgroundStyle !== 'SOLID') {
        (config as any).backgroundStyle = 'SOLID';
      }
      if (config.trailLength) {
        config.trailLength = Math.min(config.trailLength, 6);
      }
      // Force aggressive GPU low-detail LOD fallback
      (config as any).aggressiveLOD = true;
    } else if (utilization >= this.warningThreshold) {
      // Warning strain: scale back gracefully
      if (config.gpuParticlesEnabled !== false) {
        (config as any).collisionEffectParticleCount = Math.min((config as any).collisionEffectParticleCount || 20, 8);
        (config as any).maxParticles = 2000;
      }
      if (config.trailLength) {
        config.trailLength = Math.min(config.trailLength, 12);
      }
      (config as any).aggressiveLOD = false;
    } else {
      // Safe, green VRAM levels: let system defaults rule
      (config as any).aggressiveLOD = false;
    }
  }
}
